import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, fromEvent, merge } from 'rxjs';
import { takeUntil, distinctUntilChanged, debounceTime } from 'rxjs/operators';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';

export interface OfflineData {
  timestamp: Date;
  key: string;
  data: any;
  url?: string;
  method?: string;
  synced: boolean;
}

export interface NetworkStatus {
  isOnline: boolean;
  isSlowConnection: boolean;
  connectionType: string;
  effectiveType: string;
  downlink: number;
  rtt: number;
  saveData: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class OfflineService implements OnDestroy {
  private destroy$ = new Subject<void>();
  
  // Network status tracking
  private networkStatusSubject = new BehaviorSubject<NetworkStatus>(this.getNetworkStatus());
  public networkStatus$ = this.networkStatusSubject.asObservable().pipe(distinctUntilChanged());
  
  // Offline data storage
  private offlineDataKey = 'rarr-offline-data';
  private syncQueueKey = 'rarr-sync-queue';
  
  // Service worker update tracking
  private updateAvailableSubject = new BehaviorSubject<boolean>(false);
  public updateAvailable$ = this.updateAvailableSubject.asObservable();
  
  // Sync status
  private syncInProgressSubject = new BehaviorSubject<boolean>(false);
  public syncInProgress$ = this.syncInProgressSubject.asObservable();

  constructor(private swUpdate: SwUpdate) {
    this.setupNetworkListener();
    this.setupServiceWorkerUpdates();
    this.schedulePeriodicSync();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupNetworkListener(): void {
    // Listen for online/offline events
    merge(
      fromEvent(window, 'online'),
      fromEvent(window, 'offline')
    ).pipe(
      debounceTime(100),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.updateNetworkStatus();
    });

    // Listen for connection changes (if supported)
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      if (connection) {
        fromEvent(connection, 'change')
          .pipe(debounceTime(500), takeUntil(this.destroy$))
          .subscribe(() => {
            this.updateNetworkStatus();
          });
      }
    }
  }

  private setupServiceWorkerUpdates(): void {
    if (this.swUpdate.isEnabled) {
      // Check for updates
      this.swUpdate.versionUpdates
        .pipe(takeUntil(this.destroy$))
        .subscribe(event => {
          if (event.type === 'VERSION_READY') {
            this.updateAvailableSubject.next(true);
            console.log('New version available');
          }
        });

      // Check for updates every 6 hours
      setInterval(() => {
        if (this.isOnline()) {
          this.swUpdate.checkForUpdate().then(updateFound => {
            if (updateFound) {
              console.log('Checking for updates...');
            }
          }).catch(err => {
            console.error('Error checking for updates:', err);
          });
        }
      }, 6 * 60 * 60 * 1000);
    }
  }

  private schedulePeriodicSync(): void {
    // Sync every 30 seconds when online
    setInterval(() => {
      if (this.isOnline() && !this.syncInProgressSubject.value) {
        this.syncOfflineData();
      }
    }, 30000);
  }

  private updateNetworkStatus(): void {
    const newStatus = this.getNetworkStatus();
    this.networkStatusSubject.next(newStatus);

    // Trigger sync when coming back online
    if (newStatus.isOnline && !this.syncInProgressSubject.value) {
      setTimeout(() => this.syncOfflineData(), 1000);
    }
  }

  private getNetworkStatus(): NetworkStatus {
    const isOnline = navigator.onLine;
    let connectionInfo = {
      connectionType: 'unknown',
      effectiveType: 'unknown',
      downlink: 0,
      rtt: 0,
      saveData: false
    };

    // Get connection info if available
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      if (connection) {
        connectionInfo = {
          connectionType: connection.type || 'unknown',
          effectiveType: connection.effectiveType || 'unknown',
          downlink: connection.downlink || 0,
          rtt: connection.rtt || 0,
          saveData: connection.saveData || false
        };
      }
    }

    return {
      isOnline,
      isSlowConnection: connectionInfo.effectiveType === 'slow-2g' || connectionInfo.effectiveType === '2g',
      ...connectionInfo
    };
  }

  // Public API methods
  isOnline(): boolean {
    return this.networkStatusSubject.value.isOnline;
  }

  isSlowConnection(): boolean {
    return this.networkStatusSubject.value.isSlowConnection;
  }

  getNetworkInfo(): NetworkStatus {
    return this.networkStatusSubject.value;
  }

  // Offline data management
  storeOfflineData(key: string, data: any, url?: string, method?: string): void {
    const offlineData: OfflineData = {
      timestamp: new Date(),
      key,
      data,
      url,
      method,
      synced: false
    };

    const existingData = this.getOfflineData();
    existingData[key] = offlineData;
    
    localStorage.setItem(this.offlineDataKey, JSON.stringify(existingData));
    console.log('Data stored offline:', key);
  }

  getOfflineData(): { [key: string]: OfflineData } {
    try {
      const data = localStorage.getItem(this.offlineDataKey);
      return data ? JSON.parse(data) : {};
    } catch (error) {
      console.error('Error reading offline data:', error);
      return {};
    }
  }

  getOfflineDataByKey(key: string): OfflineData | null {
    const allData = this.getOfflineData();
    return allData[key] || null;
  }

  removeOfflineData(key: string): void {
    const existingData = this.getOfflineData();
    delete existingData[key];
    localStorage.setItem(this.offlineDataKey, JSON.stringify(existingData));
  }

  clearOfflineData(): void {
    localStorage.removeItem(this.offlineDataKey);
    localStorage.removeItem(this.syncQueueKey);
  }

  // Sync queue management
  addToSyncQueue(operation: { url: string; method: string; data?: any; key: string }): void {
    const queue = this.getSyncQueue();
    queue.push({
      ...operation,
      timestamp: new Date().toISOString(),
      id: Date.now().toString()
    });
    localStorage.setItem(this.syncQueueKey, JSON.stringify(queue));
    console.log('Added to sync queue:', operation);
  }

  getSyncQueue(): any[] {
    try {
      const queue = localStorage.getItem(this.syncQueueKey);
      return queue ? JSON.parse(queue) : [];
    } catch (error) {
      console.error('Error reading sync queue:', error);
      return [];
    }
  }

  // Sync operations
  async syncOfflineData(): Promise<void> {
    if (!this.isOnline() || this.syncInProgressSubject.value) {
      return;
    }

    this.syncInProgressSubject.next(true);
    const syncQueue = this.getSyncQueue();
    
    if (syncQueue.length === 0) {
      this.syncInProgressSubject.next(false);
      return;
    }

    console.log(`Starting sync of ${syncQueue.length} operations...`);
    const successfulSyncs: string[] = [];

    for (const operation of syncQueue) {
      try {
        await this.syncOperation(operation);
        successfulSyncs.push(operation.id);
        
        // Mark offline data as synced if exists
        if (operation.key) {
          const offlineData = this.getOfflineData();
          if (offlineData[operation.key]) {
            offlineData[operation.key].synced = true;
            localStorage.setItem(this.offlineDataKey, JSON.stringify(offlineData));
          }
        }
      } catch (error) {
        console.error('Failed to sync operation:', operation, error);
        // Keep failed operations in queue for retry
      }
    }

    // Remove successful syncs from queue
    if (successfulSyncs.length > 0) {
      const remainingQueue = syncQueue.filter(op => !successfulSyncs.includes(op.id));
      localStorage.setItem(this.syncQueueKey, JSON.stringify(remainingQueue));
      console.log(`Successfully synced ${successfulSyncs.length} operations`);
    }

    this.syncInProgressSubject.next(false);
  }

  private async syncOperation(operation: any): Promise<void> {
    const options: RequestInit = {
      method: operation.method,
      headers: {
        'Content-Type': 'application/json',
      }
    };

    if (operation.data && operation.method !== 'GET') {
      options.body = JSON.stringify(operation.data);
    }

    // Add authentication if available
    const token = localStorage.getItem('loginToken');
    if (token) {
      (options.headers as any)['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(operation.url, options);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Service worker update methods
  async activateUpdate(): Promise<void> {
    if (this.swUpdate.isEnabled && this.updateAvailableSubject.value) {
      try {
        await this.swUpdate.activateUpdate();
        this.updateAvailableSubject.next(false);
        window.location.reload();
      } catch (error) {
        console.error('Error activating update:', error);
        throw error;
      }
    }
  }

  // Storage management
  getStorageUsage(): { used: number; quota: number; percentage: number } {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      navigator.storage.estimate().then(estimate => {
        const used = estimate.usage || 0;
        const quota = estimate.quota || 0;
        return {
          used,
          quota,
          percentage: quota > 0 ? (used / quota) * 100 : 0
        };
      });
    }
    
    return { used: 0, quota: 0, percentage: 0 };
  }

  // Cache management
  async clearApplicationCache(): Promise<void> {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      console.log('Application cache cleared');
    }
  }

  // Utility methods
  getOfflineDataCount(): number {
    const data = this.getOfflineData();
    return Object.keys(data).length;
  }

  getSyncQueueCount(): number {
    return this.getSyncQueue().length;
  }

  getUnsyncedDataCount(): number {
    const data = this.getOfflineData();
    return Object.values(data).filter(item => !item.synced).length;
  }

  // Export/Import for debugging
  exportOfflineData(): string {
    return JSON.stringify({
      offlineData: this.getOfflineData(),
      syncQueue: this.getSyncQueue(),
      networkStatus: this.getNetworkInfo(),
      timestamp: new Date().toISOString()
    }, null, 2);
  }

  importOfflineData(jsonData: string): void {
    try {
      const data = JSON.parse(jsonData);
      if (data.offlineData) {
        localStorage.setItem(this.offlineDataKey, JSON.stringify(data.offlineData));
      }
      if (data.syncQueue) {
        localStorage.setItem(this.syncQueueKey, JSON.stringify(data.syncQueue));
      }
      console.log('Offline data imported successfully');
    } catch (error) {
      console.error('Error importing offline data:', error);
      throw error;
    }
  }
}